/** Shared controls for the Today screen. */
import type { ReactNode } from 'react';

export type TickState = 'off' | 'on' | 'writing' | 'failed';

/**
 * A tick target. 44pt minimum, and it renders OBSERVED state: `on` appears only once a
 * write has returned. While a write is in flight it shows as in flight, and a failed
 * write leaves it off with the error attached.
 */
export function Tick({ state, label, onToggle, disabled }: {
  state: TickState; label: string; onToggle: () => void; disabled?: boolean;
}) {
  const cls = `tick tick-${state}`;
  const aria = state === 'writing' ? `${label}, saving`
    : state === 'failed' ? `${label}, save failed`
    : state === 'on' ? `${label}, done` : label;
  return (
    <button
      className={cls}
      onClick={onToggle}
      disabled={disabled || state === 'writing'}
      aria-pressed={state === 'on'}
      aria-label={aria}
      type="button"
    >
      {state === 'writing' ? '·' : state === 'failed' ? '!' : state === 'on' ? '✓' : ''}
    </button>
  );
}

/** A wide tick target for a whole row (a meal slot, a habit). */
export function TickBar({ state, title, sub, right, onToggle, disabled }: {
  state: TickState; title: ReactNode; sub?: ReactNode; right?: ReactNode;
  onToggle: () => void; disabled?: boolean;
}) {
  return (
    <button
      className={`tickbar tickbar-${state}`}
      onClick={onToggle}
      disabled={disabled || state === 'writing'}
      aria-pressed={state === 'on'}
      type="button"
    >
      <span className={`tickbox tick-${state}`} aria-hidden="true">
        {state === 'writing' ? '·' : state === 'failed' ? '!' : state === 'on' ? '✓' : ''}
      </span>
      <span className="tickbar-text">
        <span className="tickbar-title">{title}</span>
        {sub && <span className="tickbar-sub">{sub}</span>}
      </span>
      {right && <span className="tickbar-right">{right}</span>}
    </button>
  );
}

/** Ticked against planned. Shows the planned figure, which is summed from the meals;
 *  there is no target constant anywhere. */
export function Meter({ label, done, total, unit, dp = 0 }: {
  label: string; done: number; total: number; unit: string; dp?: number;
}) {
  const pct = total === 0 ? 0 : Math.min(100, (done / total) * 100);
  const fmt = (n: number) => n.toFixed(dp);
  return (
    <div className="meter">
      <div className="meter-head">
        <span className="meter-label">{label}</span>
        <span className="meter-val">
          <strong>{fmt(done)}</strong>
          <span className="meter-of"> / {fmt(total)} {unit}</span>
        </span>
      </div>
      <div className="meter-track" role="progressbar" aria-valuenow={Math.round(pct)}
           aria-valuemin={0} aria-valuemax={100} aria-label={label}>
        <div className="meter-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function Ring({ done, total }: { done: number; total: number }) {
  const pct = total === 0 ? 0 : Math.min(1, done / total);
  const r = 26, circ = 2 * Math.PI * r;
  return (
    <svg className="ring" width="64" height="64" viewBox="0 0 64 64" role="img"
         aria-label={`${done} of ${total} complete`}>
      <circle cx="32" cy="32" r={r} className="ring-track" />
      {pct > 0 && (
        <circle cx="32" cy="32" r={r} className="ring-fill"
                strokeDasharray={`${circ * pct} ${circ}`} transform="rotate(-90 32 32)" />
      )}
      <text x="32" y="33" className="ring-text" dominantBaseline="middle" textAnchor="middle">
        {done}/{total}
      </text>
    </svg>
  );
}

export function Banner({ kind, title, children }: {
  kind: 'ok' | 'warn' | 'bad' | 'info'; title?: ReactNode; children: ReactNode;
}) {
  return (
    <div className={`banner ${kind}`}>
      {title && <strong>{title}</strong>}
      <div style={title ? { marginTop: 5 } : undefined}>{children}</div>
    </div>
  );
}
